/**
 * DOCUMENTATION:
 * Component: Html5MultimediaGraphics
 * Purpose: Educational page covering HTML5 native multimedia tags (audio, video) and graphic containers (SVG, Canvas).
 * * * Note for Junior Developers:
 * - HTML5 revolutionized the web by natively supporting media, eliminating the need for bulky plugins like Adobe Flash.
 * - Always provide fallback content inside media tags (`<video>`, `<audio>`) for older browsers or accessibility reasons.
 * - `useMemo` is used again here to cache our curriculum data, keeping our render cycles lean and performant.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function Html5MultimediaGraphics() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Multimedia Elements reference table
    const multimediaElements = useMemo(() => [
        { tag: "<video>", purpose: "Video Playback", description: "Embeds a media player for video playback. Supports attributes like controls, autoplay, loop, and poster." },
        { tag: "<audio>", purpose: "Audio Playback", description: "Embeds sound content. Similar to <video>, it utilizes the 'controls' attribute to display a native browser player." },
        { tag: "<track>", purpose: "Subtitles & Captions", description: "Used as a child of <video> or <audio> to specify timed text tracks (e.g., .vtt files for subtitles)." },
        { tag: "<svg>", purpose: "Vector Graphics", description: "Defines Scalable Vector Graphics directly in HTML. SVGs do not lose quality when zoomed or resized." },
        { tag: "<canvas>", purpose: "Scriptable Graphics", description: "Provides an empty bitmap area that can be drawn upon using JavaScript (great for games, charts, and animations)." },
        { tag: "<iframe>", purpose: "Inline Frame", description: "Embeds another HTML document within the current one (commonly used for embedding YouTube videos or Google Maps)." },
    ], []);

    // Coding examples demonstrating multimedia implementation
    const codingModules = useMemo(() => [
        {
            title: "1. Accessible Video Player",
            code: `<video width="100%" height="auto" controls poster="/images/thumbnail.jpg">\n  \n  <source src="lecture.mp4" type="video/mp4">\n  <source src="lecture.webm" type="video/webm">\n  \n  \n  <track kind="captions" src="captions_en.vtt" srclang="en" label="English" default>\n  \n  \n  Your browser does not support the video tag.\n</video>`,
            desc: "The 'controls' attribute adds play, pause, and volume buttons. The 'poster' attribute shows an image while the video downloads. Providing a <track> ensures deaf or hard-of-hearing users can access the content."
        },
        {
            title: "2. Inline SVG (Scalable Vector Graphics)",
            code: `<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">\n  \n  <circle cx="50" cy="50" r="40" stroke="goldenrod" stroke-width="4" fill="rgba(16,7,32,0.8)" />\n  \n  \n  <text x="50%" y="50%" text-anchor="middle" fill="white" dy=".3em" font-family="sans-serif">\n    HTML5\n  </text>\n</svg>`,
            desc: "Unlike JPGs or PNGs (which are pixel-based), SVGs are math-based. You can style them directly with CSS and manipulate them with JavaScript. They are perfect for logos and icons."
        },
        {
            title: "3. Embedding External Content (Iframe)",
            code: `\n<iframe \n  width="560" \n  height="315" \n  src="https://www.youtube.com/embed/dQw4w9WgXcQ" \n  title="YouTube video player" \n  frameborder="0" \n  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" \n  allowfullscreen>\n</iframe>`,
            desc: "Iframes allow you to inject a secure sandbox of another website into yours. For security, modern browsers strictly control what an iframe can do via the 'allow' attribute."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    MULTIMEDIA & GRAPHICS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            The Rich Web Experience
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Before HTML5, embedding audio and video required clunky third-party plugins that drained battery life and introduced massive security flaws. Today, HTML5 treats media as first-class citizens. Furthermore, powerful graphic APIs like <code>&lt;svg&gt;</code> and <code>&lt;canvas&gt;</code> allow developers to draw, animate, and render high-performance graphics directly in the browser.
                        </Typography>

                        {/* ✅ Multimedia Elements Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Media & Graphic Tags</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Tag</div>
                                    <div>Primary Purpose</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {multimediaElements.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.tag}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.purpose}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/html5-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
